import React from 'react';
import { getImage } from 'gatsby-plugin-image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { graphql, useStaticQuery } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navigation } from 'swiper';

import 'swiper/css/bundle';
import 'swiper/css/navigation';
import useResizer from '@components/extra/useResizer';
import RawHTML from '@components/Common/RawHTML/index';
import { cn } from '@/lib/utils';
import { dataScienceProjects } from '@src/constants/data-science';
import useDataScienceProgram from '@src/hooks/useDataScienceProgram';
import { getProgramTypeFromPath } from '@src/constants/pages';
import { useLocation } from '@reach/router';

const ProjectsCarouselV2 = ({ type, ...props }) => {
  const isWithoutLabels = props.isWithoutLabels;
  const { isDataSciencePage, isDataScienceProvisionalPage } =
    useDataScienceProgram();
  const location = useLocation();
  const pathname = location.pathname;
  const programType = getProgramTypeFromPath(pathname);
  const isSkillQDataAnalyticsProgram =
    programType === 'SKILLQ_DATA_ANALYTICS_PROGRAM';

  const navigationPrevRef = React.useRef(null);
  const navigationNextRef = React.useRef(null);
  const swiperRef = React.useRef(null);
  const cardsContainerRef = React.useRef(null);
  const isMobile = useResizer();
  const [selectedFilter, setSelectedFilter] = React.useState('All');
  // Uniform card height across every slide (= the tallest card anywhere) and the
  // index of the currently visible slide. Together these let us center the nav
  // arrows on the actual rows of the current slide: cardH/2 for a 1-row slide,
  // and (2·cardH + gap)/2 — i.e. the gap between the cards — for a 2-row slide.
  const [cardHeight, setCardHeight] = React.useState(0);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const data = useStaticQuery(graphql`
    query {
      projects: allFile(
        filter: { relativePath: { regex: "images/v5/ProjectsCards/" } }
      ) {
        nodes {
          name
          childImageSharp {
            gatsbyImageData(
              blurredOptions: {}
              width: 400
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
  `);

  const allProjects = props.projects
    ? props.projects
    : isDataSciencePage ||
      isSkillQDataAnalyticsProgram ||
      isDataScienceProvisionalPage
    ? dataScienceProjects
    : [
          {
            title: <>QKart: E-commerce Test Automation</>,
            subtitle: (
              <>
                Build a complete automation suite for a Flipkart-like e-commerce{' '}
                <br></br>
                application. Master Page Object Model, TestNG, Cucumber BDD, and{' '}
                <br></br>
                data-driven testing with Apache POI.
              </>
            ),
            points: (
              <>
                <ul>
                  <li>Build robust Selenium automation frameworks</li>
                  <li>Implement data-driven testing with POI</li>
                  <li>Master Page Object Model architecture</li>
                </ul>
              </>
            ),
            tags: ['QA', 'SELENIUM'],
            skills: ['MongoDB', 'React JS', 'Node JS', 'Express JS'],
          },
          {
            title: (
              <>
                Build an OTT platform<br></br>like Netflix Engineers
              </>
            ),
            subtitle: (
              <>
                Build a video streaming fullstack app<br></br> from design to
                deployment with GenAI
              </>
            ),
            tag: 'Independent Project',
            image: 'CardTwo',
            skills: ['Prompt Engineering', 'LLM', 'Cursor', 'OpenAI'],
          },
          {
            title: (
              <>
                Work like Backend<br></br>Developers at Zomato
              </>
            ),
            subtitle: (
              <>
                Build a high-scale distributed web<br></br>backend for a food
                ordering app.
              </>
            ),
            tag: 'Guided Project',
            image: 'CardFour',
            skills: ['MongoDB', 'Java', 'Spring Boot', 'Redis'],
          },
          {
            title: (
              <>
                Experience building a<br></br>Google News like app
              </>
            ),
            subtitle: (
              <>
                Build a fully functional news<br></br>aggregator like google
                news.
              </>
            ),
            tag: 'Independent Project',
            image: 'CardSix',
            skills: ['HTML', 'CSS', 'Rest APIs', 'Flexbox'],
          },
          {
            title: (
              <>
                Work like Frontend<br></br> Developers at AirBnB
              </>
            ),
            subtitle: (
              <>
                Build a highly responsive<br></br>frontend for a travel app.
              </>
            ),
            tag: 'Guided Project',
            image: 'CardThree',
            skills: ['HTML', 'CSS', 'Bootstrap', 'React JS'],
          },
          {
            title: (
              <>
                Work like an<br></br>Engineer at Zerodha
              </>
            ),
            subtitle: (
              <>
                Build a tool to analyse an<br></br>investment portfolio.
              </>
            ),
            tag: 'Guided Project',
            image: 'CardFive',
            skills: ['OOP', 'JSON', 'Gradle', 'Rest API'],
          },
          {
            title: (
              <>
                Work like a senior<br></br>developer at Flipkart
              </>
            ),
            subtitle: (
              <>
                Experience working on a live<br></br>distributed system in AWS.
              </>
            ),
            tag: 'Guided Project',
            image: 'CardSeven',
            skills: ['Microservices', 'Kafka', 'Scaling', 'Cloud'],
          },
        ];

  // Filter projects based on selected filter
  const projects = React.useMemo(() => {
    if (selectedFilter === 'All') {
      return allProjects;
    }

    return allProjects.filter((project) => {
      const tags = project.tags || [];
      const hasFDT = Array.isArray(tags)
        ? tags.includes('FDT')
        : tags === 'FDT';
      const hasBDT = Array.isArray(tags)
        ? tags.includes('BDT')
        : tags === 'BDT';
      const hasQA = Array.isArray(tags) ? tags.includes('QA') : tags === 'QA';
      const hasAdvancedQA = Array.isArray(tags)
        ? tags.includes('ADVANCED')
        : tags === 'ADVANCED';
      const hasDA = Array.isArray(tags) ? tags.includes('DA') : tags === 'DA';
      const hasDS = Array.isArray(tags) ? tags.includes('DS') : tags === 'DS';

      if (selectedFilter === 'Data Analysis') {
        return hasDA;
      }
      if (selectedFilter === 'Data Science') {
        return hasDS;
      }
      if (selectedFilter === 'FDT') {
        return hasFDT;
      }
      if (selectedFilter === 'BDT') {
        return hasBDT;
      }
      if (selectedFilter === 'QA') {
        return hasQA;
      }
      if (selectedFilter === 'ADVANCED QA') {
        return hasAdvancedQA;
      }
      if (selectedFilter === 'OTHERS') {
        if (type === 'QA') {
          return !hasQA && !hasAdvancedQA;
        }
        return !hasFDT && !hasBDT;
      }
      return true;
    });
  }, [allProjects, selectedFilter, type]);

  // Group projects into chunks of 4 for the 2x2 grid
  const projectGroups = [];
  for (let i = 0; i < projects.length; i += 4) {
    projectGroups.push(projects.slice(i, i + 4));
  }

  // Measure the tallest card across all slides and use it as the height for
  // every card (via minHeight on each cell), so cards are uniform in both the
  // 1-row and 2-row layouts. Re-measures on filter change, resize and content
  // reflow (images loading). Converges in one step: once minHeight is applied
  // the max no longer changes.
  React.useEffect(() => {
    const container = cardsContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const cells = () => container.querySelectorAll('.pc-card-cell');
    const measure = () => {
      let max = 0;
      cells().forEach((c) => {
        max = Math.max(max, c.getBoundingClientRect().height);
      });
      if (max) setCardHeight(max);
    };
    measure();
    const observer = new ResizeObserver(measure);
    cells().forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, [selectedFilter, projects.length, isMobile]);

  const GRID_GAP = 24; // matches gap-6 (1.5rem) on the card grid
  const columns = isMobile ? 1 : 2;
  const activeCardCount =
    projectGroups[activeIndex]?.length || projectGroups[0]?.length || 1;
  const activeRows = Math.max(1, Math.ceil(activeCardCount / columns));
  // Vertical center of the current slide's card rows.
  const arrowTop = cardHeight
    ? (activeRows * cardHeight + (activeRows - 1) * GRID_GAP) / 2
    : undefined;

  const settings = {
    slidesPerView: 1,
    id: 'projects-carousel-v2',
    loop: projectGroups.length > 1,
    spaceBetween: 30,
    speed: 300,
    grabCursor: true,
    autoHeight: true,
    // autoplay: {
    //   delay: 5000,
    //   disableOnInteraction: true,
    // },
    enabled: projectGroups.length > 1 || isMobile,
  };

  const ProjectCard = ({ project }) => {
    const projectImage = data.projects.nodes.find(
      (ele) => ele.name == project.image,
    );
    const image = getImage(projectImage?.childImageSharp);

    return (
      <div
        className={cn(
          'items-left flex h-full flex-col justify-start gap-4 rounded-[20px] bg-v5-neutral-200 p-5',
          `${isWithoutLabels ? 'justify-start' : ''}`,
        )}
      >
        {project.badge ? (
          <span className="w-fit rounded-md bg-v5-yellow-200 px-2 py-2 font-rubik text-xs font-bold text-black">
            {project.badge}
          </span>
        ) : project.tags ? (
          <span className="w-fit rounded-md bg-v5-yellow-200 px-2 py-2 font-rubik text-xs font-bold uppercase text-black">
            {Array.isArray(project.tags)
              ? project.tags.join(' • ')
              : project.tags}
          </span>
        ) : (
          <></>
        )}
        <div className="text-left">
          <h3 className={`my-2 font-manrope text-lg font-black`}>
            <RawHTML>{project.title}</RawHTML>
          </h3>
          {project.role ? (
            <p className="mb-1 font-rubik text-sm font-bold text-v5-neutral-500">
              <RawHTML>{project.role}</RawHTML>
            </p>
          ) : (
            <></>
          )}
          <p className="max-w-[80%] font-rubik text-sm text-v5-neutral-400">
            <RawHTML>{project.subtitle}</RawHTML>
          </p>
        </div>
        {project.skills && project.skills.length ? (
          <div className="flex flex-wrap justify-start gap-x-2 gap-y-2">
            {project.skills.map((el) => (
              <div
                className="min-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap rounded-md border-2 border-black py-1 px-3 text-center font-rubik text-xs font-bold"
                key={el}
              >
                {el}
              </div>
            ))}
          </div>
        ) : (
          <></>
        )}
        {project.points ? (
          <div className="text-left text-sm text-v5-neutral-400">
            <RawHTML>{project.points}</RawHTML>
          </div>
        ) : (
          <></>
        )}
      </div>
    );
  };

  return (
    <div className="relative w-full" id="projects-carousel-v2">
      <div className="mb-6 flex w-full justify-center">
        <div className="flex rounded-md border-[0.5px] border-black p-2">
          {(isDataSciencePage
            ? ['All', 'Data Analysis', 'Data Science']
            : type === 'FSP' || type === 'FSD'
            ? ['All', 'FDT', 'BDT', 'OTHERS']
            : type === 'QA'
            ? ['All', 'QA', 'ADVANCED QA', 'OTHERS']
            : ['All', 'FDT', 'BDT', 'OTHERS']
          ).map((filter) => (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter)}
              className={`rounded-md px-4 py-2 font-rubik text-sm font-bold uppercase ${
                selectedFilter === filter
                  ? 'bg-v5-green-200 text-white drop-shadow-md'
                  : 'bg-white text-black'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>
      <div className="relative" ref={cardsContainerRef}>
        <Swiper
          key={selectedFilter}
          ref={swiperRef}
          {...settings}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          modules={[Navigation]}
          className={`projectCarousalV2 w-full`}
        >
          {projectGroups.map((group, groupIndex) => (
            <SwiperSlide key={groupIndex} className="h-auto">
              <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2">
                {group.map((project, projectIndex) => (
                  <div
                    key={`${groupIndex}-${projectIndex}`}
                    className="pc-card-cell h-full"
                    style={cardHeight ? { minHeight: cardHeight } : undefined}
                  >
                    <ProjectCard project={project} />
                  </div>
                ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {(projectGroups.length > 1 || isMobile) && (
          <>
            <div
              onClick={() => swiperRef.current.swiper.slidePrev()}
              ref={navigationPrevRef}
              style={arrowTop ? { top: arrowTop } : undefined}
              className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer hover:opacity-75 sm:-left-10"
            >
              <FontAwesomeIcon icon={['fas', 'chevron-left']}></FontAwesomeIcon>
            </div>
            <div
              onClick={() => swiperRef.current.swiper.slideNext()}
              ref={navigationNextRef}
              style={arrowTop ? { top: arrowTop } : undefined}
              className="absolute top-1/2 right-0 z-10 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer hover:opacity-75 sm:-right-10"
            >
              <FontAwesomeIcon icon={['fas', 'chevron-right']}></FontAwesomeIcon>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectsCarouselV2;
